/**
 * New promotion (`/business/promotions/new`, DLG-002 / ANTI-006, TMMRSL-0073): the create form as a
 * page, with a live cost preview beside it (below it under `md`). The form keeps 0071's model —
 * noValidate, field errors with the first invalid field focused, required marking, `SubmitButton`
 * — and the unsaved-changes guard. On success it toasts and goes to the list with the new
 * promotion selected (`?promotion=<id>`, 0072's key), replacing this page in history. STAFF is
 * read-only everywhere and a create page has nothing to show read-only: once the operator is known
 * and can't write, the page replaces itself with the list (never while the operator is still loading,
 * and before the form mounts, so the unsaved guard can't fire).
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/errors';
import { useCanWrite, useCurrentOperator } from '@/auth/authHooks';
import { mapFieldErrors } from '@/api/fieldErrors';
import type { PromotionWriteRequest } from '@/api/generated';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { PageHeader } from '@/components/PageHeader';
import { SubmitButton } from '@/components/SubmitButton';
import { useActionError, type ActionError } from '@/components/useActionError';
import { useFieldErrors } from '@/components/useFieldErrors';
import { useToast } from '@/components/useToast';
import { useUnsavedGuard } from '@/components/useUnsavedGuard';
import { moneyInputExample, parseMoneyToMinor, percentToBp } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useTenantApi, useTenantCurrency, useTenantLink } from '@/tenant/useTenant';
import { PromotionCostPreview } from './PromotionCostCard';
import { costBasisOf, type PromotionDiscount } from './promotionMath';
import { useCostCards } from './usePromotions';

export type DiscountKind = 'percent' | 'amount' | 'free' | 'trial';

const CODE_PATTERN = /^[A-Z0-9_-]{3,32}$/;

type PromoField = 'code' | 'name' | 'percent' | 'amount' | 'months' | 'days';

const PROMO_FIELDS = {
  code: 'code',
  name: 'name',
  percent: 'percent',
  percentBp: 'percent',
  amount: 'amount',
  amountMinor: 'amount',
  months: 'months',
  statements: 'months',
  days: 'days',
  trialDays: 'days',
} as const satisfies Record<string, PromoField>;

const PROMO_ORDER = ['code', 'name', 'percent', 'amount', 'months', 'days'] as const;

const INITIAL = {
  code: '',
  name: '',
  kind: 'percent' as DiscountKind,
  percent: '',
  amount: '',
  months: '1',
  days: '14',
  duration: '3',
  cap: '50',
  newOnly: true,
};

type Fields = typeof INITIAL;

/**
 * The draft's discount for the preview, or `null` while it has none it could create: an empty or
 * unparseable percent or amount, or a percent past 100.
 */
function draftDiscount(fields: Fields, currency: string, locale: string): PromotionDiscount | null {
  switch (fields.kind) {
    case 'percent': {
      const percentBp = percentToBp(fields.percent, locale);
      return percentBp === null || percentBp <= 0 || percentBp > 10000
        ? null
        : { kind: 'PERCENT_OFF_VARIABLE', percentBp };
    }
    case 'amount': {
      const amountMinor =
        currency === '' ? null : parseMoneyToMinor(fields.amount, currency, locale);
      return amountMinor === null || amountMinor <= 0
        ? null
        : { kind: 'AMOUNT_OFF_VARIABLE', amountMinor };
    }
    case 'free':
      return { kind: 'PERCENT_OFF_VARIABLE', percentBp: 10000 };
    default:
      return { kind: 'TRIAL_DAYS' };
  }
}

export function NewPromotionScreen() {
  const { t } = useTranslation();
  const operator = useCurrentOperator();
  const canWrite = useCanWrite();
  const link = useTenantLink();
  if (operator === null) {
    return (
      <Box role="status" aria-label={t('common.loading')}>
        <Skeleton variant="rounded" height={320} />
      </Box>
    );
  }
  if (!canWrite) return <Navigate to={link('promotions')} replace />;
  return <NewPromotionForm />;
}

function NewPromotionForm() {
  const { t } = useTranslation();
  const describe = useActionError();
  const locale = useLocale();
  const api = useTenantApi();
  const link = useTenantLink();
  const navigate = useNavigate();
  const showToast = useToast();
  const codeRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const percentRef = useRef<HTMLInputElement>(null);
  const amountRef = useRef<HTMLInputElement>(null);
  const monthsRef = useRef<HTMLInputElement>(null);
  const daysRef = useRef<HTMLInputElement>(null);
  const currency = useTenantCurrency();
  const noCurrency = currency === '';
  const cards = useCostCards();
  const [fields, setFields] = useState<Fields>(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ActionError | null>(null);
  // Set once the promotion exists: the guard lets go, then the page leaves for the list.
  const [createdId, setCreatedId] = useState<number | null>(null);
  const fieldErrors = useFieldErrors(
    PROMO_ORDER,
    {
      code: codeRef,
      name: nameRef,
      percent: percentRef,
      amount: amountRef,
      months: monthsRef,
      days: daysRef,
    },
    submitting,
  );
  const listPath = link('promotions');
  const dirty = createdId === null && JSON.stringify(fields) !== JSON.stringify(INITIAL);
  const guard = useUnsavedGuard({ when: dirty, name: t('unsaved.names.promotion') });

  // Navigation waits for the render that released the guard.
  useEffect(() => {
    if (createdId === null) return;
    void navigate(
      { pathname: listPath, search: `?promotion=${createdId}` },
      { replace: true },
    );
  }, [createdId, listPath, navigate]);

  /** The request body, or the message for each field that can't make one (TMM-B5). */
  function check(): {
    body: PromotionWriteRequest | null;
    errors: Partial<Record<PromoField, string>>;
  } {
    const errors: Partial<Record<PromoField, string>> = {};
    const code = fields.code.trim();
    const name = fields.name.trim();
    if (!CODE_PATTERN.test(code)) errors.code = t('validation.codePattern');
    if (name.length === 0) errors.name = t('validation.name');
    const maxRedemptions = fields.cap === 'unlimited' ? null : Number(fields.cap);
    const statements = fields.duration === 'lifetime' ? null : Number(fields.duration);
    // A key with no value is left out (TMMAPI-0167): unlimited redemptions and lifetime statements.
    const empty: PromotionWriteRequest = {
      code,
      name,
      kind: 'PERCENT_OFF_VARIABLE',
      newSubscribersOnly: fields.newOnly,
      ...(maxRedemptions !== null ? { maxRedemptions } : {}),
    };
    const limited = statements !== null ? { statements } : {};

    let body: PromotionWriteRequest | null = null;
    if (fields.kind === 'percent') {
      const percentBp = percentToBp(fields.percent, locale);
      if (percentBp === null) errors.percent = t('validation.percent');
      else if (percentBp <= 0 || percentBp > 10000) errors.percent = t('validation.percentRange');
      else body = { ...empty, ...limited, kind: 'PERCENT_OFF_VARIABLE', percentBp };
    } else if (fields.kind === 'amount') {
      const amountMinor = noCurrency ? null : parseMoneyToMinor(fields.amount, currency, locale);
      if (amountMinor === null) {
        errors.amount = t('validation.amountMoney', {
          example: moneyInputExample(currency, locale),
        });
      } else body = { ...empty, ...limited, kind: 'AMOUNT_OFF_VARIABLE', amountMinor, currency };
    } else if (fields.kind === 'free') {
      const months = Number(fields.months);
      if (!Number.isInteger(months) || months < 1) errors.months = t('validation.wholeNumber');
      else {
        body = { ...empty, kind: 'PERCENT_OFF_VARIABLE', percentBp: 10000, statements: months };
      }
    } else {
      const trialDays = Number(fields.days);
      if (!Number.isInteger(trialDays) || trialDays < 1) errors.days = t('validation.wholeNumber');
      else body = { ...empty, kind: 'TRIAL_DAYS', trialDays };
    }
    return { body: Object.keys(errors).length > 0 ? null : body, errors };
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (noCurrency) return;
    const { body, errors } = check();
    if (!body) {
      setError(null);
      fieldErrors.fail(errors);
      return;
    }
    setSubmitting(true);
    setError(null);
    fieldErrors.reset();
    try {
      const created = await api.createPromotion(body);
      showToast(t('promotions.toast.created', { code: body.code }));
      setCreatedId(created.id);
    } catch (err) {
      const action = describe(err);
      if (err instanceof ApiError && err.code === 'CONFLICT') {
        fieldErrors.fail({ code: t('promotions.form.codeTaken') });
      } else {
        setError(action);
        fieldErrors.failMarked(mapFieldErrors(action.fieldKeys, PROMO_FIELDS), {
          code: t('promotions.form.codeHelp'),
          name: t('promotions.form.nameLimit'),
          percent: t('errors.fieldInvalid'),
          amount: t('errors.fieldInvalid'),
          months: t('errors.fieldInvalid'),
          days: t('errors.fieldInvalid'),
        });
      }
    } finally {
      setSubmitting(false);
    }
  }

  const hideDuration = fields.kind === 'trial' || fields.kind === 'free';
  const disabled = submitting || createdId !== null;

  return (
    <Box>
      <PageHeader
        id="newPromotion"
        subtitle=""
        breadcrumbs={[
          { label: t('pages.promotions.title'), to: listPath },
          { label: t('pages.newPromotion.title') },
        ]}
      />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper sx={{ p: 2.5 }}>
            <Box component="form" noValidate onSubmit={(e) => void handleSubmit(e)}>
              {noCurrency ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t('common.noCurrency')}
                </Alert>
              ) : null}
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                {t('promotions.form.lead')}
              </Typography>
              <ActionErrorAlert error={error} sx={{ mb: 2 }} />
              <TextField
                required
                fullWidth
                margin="dense"
                label={t('promotions.form.code')}
                inputRef={codeRef}
                helperText={fieldErrors.errors.code ?? t('promotions.form.codeHelp')}
                error={fieldErrors.errors.code !== undefined}
                value={fields.code}
                onChange={(e) => {
                  fieldErrors.clear('code');
                  setFields((prev) => ({ ...prev, code: e.target.value.toUpperCase() }));
                }}
                slotProps={{ htmlInput: { maxLength: 32 } }}
                disabled={disabled}
              />
              <TextField
                required
                fullWidth
                margin="dense"
                label={t('promotions.form.name')}
                inputRef={nameRef}
                helperText={fieldErrors.errors.name ?? t('promotions.form.nameHelp')}
                error={fieldErrors.errors.name !== undefined}
                value={fields.name}
                onChange={(e) => {
                  fieldErrors.clear('name');
                  setFields((prev) => ({ ...prev, name: e.target.value }));
                }}
                disabled={disabled}
              />
              <TextField
                select
                required
                fullWidth
                margin="dense"
                label={t('promotions.form.discount')}
                value={fields.kind}
                onChange={(e) =>
                  setFields((prev) => ({ ...prev, kind: e.target.value as DiscountKind }))
                }
                disabled={disabled}
              >
                <MenuItem value="percent">{t('promotions.form.discountPercent')}</MenuItem>
                <MenuItem value="amount">{t('promotions.form.discountAmount')}</MenuItem>
                <MenuItem value="free">{t('promotions.form.discountFree')}</MenuItem>
                <MenuItem value="trial">{t('promotions.form.discountTrial')}</MenuItem>
              </TextField>
              {fields.kind === 'percent' ? (
                <TextField
                  required
                  fullWidth
                  margin="dense"
                  label={t('promotions.form.percent')}
                  value={fields.percent}
                  onChange={(e) => {
                    fieldErrors.clear('percent');
                    setFields((prev) => ({ ...prev, percent: e.target.value }));
                  }}
                  inputRef={percentRef}
                  error={fieldErrors.errors.percent !== undefined}
                  helperText={fieldErrors.errors.percent}
                  disabled={disabled}
                />
              ) : null}
              {fields.kind === 'amount' ? (
                <TextField
                  required
                  fullWidth
                  margin="dense"
                  label={t('promotions.form.amount')}
                  value={fields.amount}
                  onChange={(e) => {
                    fieldErrors.clear('amount');
                    setFields((prev) => ({ ...prev, amount: e.target.value }));
                  }}
                  inputRef={amountRef}
                  error={fieldErrors.errors.amount !== undefined}
                  helperText={fieldErrors.errors.amount}
                  disabled={disabled || noCurrency}
                />
              ) : null}
              {fields.kind === 'free' ? (
                <TextField
                  required
                  fullWidth
                  type="number"
                  margin="dense"
                  label={t('promotions.form.months')}
                  value={fields.months}
                  onChange={(e) => {
                    fieldErrors.clear('months');
                    setFields((prev) => ({ ...prev, months: e.target.value }));
                  }}
                  inputRef={monthsRef}
                  error={fieldErrors.errors.months !== undefined}
                  helperText={fieldErrors.errors.months}
                  slotProps={{ htmlInput: { min: 1, step: 1 } }}
                  disabled={disabled}
                />
              ) : null}
              {fields.kind === 'trial' ? (
                <TextField
                  required
                  fullWidth
                  type="number"
                  margin="dense"
                  label={t('promotions.form.days')}
                  value={fields.days}
                  onChange={(e) => {
                    fieldErrors.clear('days');
                    setFields((prev) => ({ ...prev, days: e.target.value }));
                  }}
                  inputRef={daysRef}
                  error={fieldErrors.errors.days !== undefined}
                  helperText={fieldErrors.errors.days}
                  slotProps={{ htmlInput: { min: 1, step: 1 } }}
                  disabled={disabled}
                />
              ) : null}
              {hideDuration ? null : (
                <TextField
                  select
                  required
                  fullWidth
                  margin="dense"
                  label={t('promotions.form.duration')}
                  value={fields.duration}
                  onChange={(e) => setFields((prev) => ({ ...prev, duration: e.target.value }))}
                  disabled={disabled}
                >
                  <MenuItem value="1">{t('promotions.form.duration1')}</MenuItem>
                  <MenuItem value="2">{t('promotions.form.duration2')}</MenuItem>
                  <MenuItem value="3">{t('promotions.form.duration3')}</MenuItem>
                  <MenuItem value="6">{t('promotions.form.duration6')}</MenuItem>
                  <MenuItem value="lifetime">{t('promotions.form.durationLifetime')}</MenuItem>
                </TextField>
              )}
              <TextField
                select
                required
                fullWidth
                margin="dense"
                label={t('promotions.form.cap')}
                value={fields.cap}
                onChange={(e) => setFields((prev) => ({ ...prev, cap: e.target.value }))}
                disabled={disabled}
              >
                <MenuItem value="10">{t('promotions.form.cap10')}</MenuItem>
                <MenuItem value="25">{t('promotions.form.cap25')}</MenuItem>
                <MenuItem value="50">{t('promotions.form.cap50')}</MenuItem>
                <MenuItem value="100">{t('promotions.form.cap100')}</MenuItem>
                <MenuItem value="unlimited">{t('promotions.form.capUnlimited')}</MenuItem>
              </TextField>
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Switch
                    checked={fields.newOnly}
                    onChange={(e) => setFields((prev) => ({ ...prev, newOnly: e.target.checked }))}
                    disabled={disabled}
                  />
                }
                label={t('promotions.form.newOnly')}
              />
              <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', mt: 2 }}>
                <Button onClick={() => void navigate(listPath)} disabled={disabled}>
                  {t('common.cancel')}
                </Button>
                {/* Disabled only with no currency, and the page says so above (TMM-B5). */}
                <SubmitButton type="submit" loading={submitting} disabled={noCurrency}>
                  {t('promotions.form.create')}
                </SubmitButton>
              </Stack>
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <PromotionCostPreview
            draft={draftDiscount(fields, currency, locale)}
            basis={cards === null ? null : costBasisOf(cards.tenantCard)}
            loading={cards === null}
          />
        </Grid>
      </Grid>
      {guard}
    </Box>
  );
}
